import { formatNumberAsCurrency } from 'utils/numberUtils';
import {
  AssignmentHistoryCascadeFinancialInfoUpdateViewModel,
  AssignmentHistoryCascadeFinancialInfoUpdateDataViewModel,
  AssignmentHistoryCascadeFinancialInfoUpdateStatus,
  HistoryTypeEnum,
} from 'api/adminApiClient';

type CustomerHistoryCascadeRateUpdateDetailsModel = {
  assignments?: AssignmentHistoryCascadeFinancialInfoUpdateViewModel[];
};

export type NormalizedCascadeAssignment = {
  assignmentKey: string;
  scheduler: boolean;
  statusEnum: AssignmentHistoryCascadeFinancialInfoUpdateStatus | undefined;
  statusLabel: string;
  detailsMessage: string | undefined;
  oldValueSummary: string;
  newValueSummary: string;
};

const STATUS_LABELS: Record<
  AssignmentHistoryCascadeFinancialInfoUpdateStatus,
  string
> = {
  [AssignmentHistoryCascadeFinancialInfoUpdateStatus.Updated]: 'Updated',
  [AssignmentHistoryCascadeFinancialInfoUpdateStatus.Failed]: 'Failed',
  [AssignmentHistoryCascadeFinancialInfoUpdateStatus.Skipped]: 'Skipped',
};

const getStatusLabel = (
  status?: AssignmentHistoryCascadeFinancialInfoUpdateStatus,
): string => {
  if (status === undefined || status === null) return 'Unknown';
  return STATUS_LABELS[status] ?? 'Unknown';
};

const formatFinancialSummary = (
  data?: AssignmentHistoryCascadeFinancialInfoUpdateDataViewModel,
): string => {
  if (!data) return '';
  const parts: string[] = [];
  const vms = formatNumberAsCurrency({ num: data.vmsFee });
  const msp = formatNumberAsCurrency({ num: data.mspFee });
  const profit = formatNumberAsCurrency({ num: data.profitRate });
  if (vms !== undefined) parts.push(`VMS: ${vms}`);
  if (msp !== undefined) parts.push(`MSP: ${msp}`);
  if (profit !== undefined) parts.push(`Profit rate: ${profit}`);
  return parts.join(', ');
};

const getCascadeTotalDetails = (
  details: unknown,
): CustomerHistoryCascadeRateUpdateDetailsModel | null => {
  if (!details || typeof details !== 'object') {
    return null;
  }
  const candidate = details as { assignments?: unknown };
  if (
    candidate.assignments !== undefined &&
    !Array.isArray(candidate.assignments)
  ) {
    return null;
  }
  return candidate as CustomerHistoryCascadeRateUpdateDetailsModel;
};

const getCascadeAssignmentsByType = ({
  type,
  details,
}: {
  type?: HistoryTypeEnum;
  details: unknown;
}): AssignmentHistoryCascadeFinancialInfoUpdateViewModel[] => {
  if (type === HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdateTotalDetails) {
    return getCascadeTotalDetails(details)?.assignments ?? [];
  }

  return [];
};

export const getNormalizedCascadeAssignments = ({
  type,
  details,
}: {
  type?: HistoryTypeEnum;
  details: unknown;
}): NormalizedCascadeAssignment[] => {
  if (type !== HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdateTotalDetails) {
    return [];
  }

  const assignments = getCascadeAssignmentsByType({ type, details });

  return assignments.map((assignment, index) => ({
    assignmentKey: assignment.assignmentKey || `Assignment ${index + 1}`,
    scheduler: Boolean(assignment.scheduler),
    statusEnum: assignment.status,
    statusLabel: getStatusLabel(assignment.status),
    detailsMessage: assignment.details || undefined,
    oldValueSummary: formatFinancialSummary(assignment.old),
    newValueSummary: formatFinancialSummary(assignment.new),
  }));
};

export const getCascadeFailedStatus = ({
  type,
  details,
}: {
  type?: HistoryTypeEnum;
  details: unknown;
}): boolean => {
  if (type === HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdate) {
    const single =
      details as
        | AssignmentHistoryCascadeFinancialInfoUpdateViewModel
        | undefined;
    if (single?.status !== undefined) {
      return (
        single.status ===
        AssignmentHistoryCascadeFinancialInfoUpdateStatus.Failed
      );
    }
    return Boolean(
      single?.old?.validationFailed || single?.new?.validationFailed,
    );
  }

  if (type === HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdateTotalDetails) {
    const assignments = getCascadeAssignmentsByType({ type, details });
    return assignments.some(
      (a) =>
        a.status === AssignmentHistoryCascadeFinancialInfoUpdateStatus.Failed,
    );
  }

  return false;
};

export const getCascadeSubline = ({
  type,
  details,
}: {
  type?: HistoryTypeEnum;
  details: unknown;
}): string | null => {
  if (type === HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdate) {
    const singleDetails =
      details as
        | AssignmentHistoryCascadeFinancialInfoUpdateViewModel
        | undefined;
    return formatFinancialSummary(singleDetails?.new) || null;
  }

  if (type === HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdateTotalDetails) {
    return null;
  }

  return null;
};
